import { useColorScheme } from 'nativewind';
import { useMemo } from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';

import type { ColorToken, Mode, ThemePalette } from './tokens';
import { tokens } from './tokens';
import { ThemePaletteContext } from './useThemePalette';
import useThemeVars, { ThemeModeContext } from './useThemeVars';

function resolveGroup(group: Record<string, ColorToken>, mode: Mode): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, token] of Object.entries(group)) {
    result[key] = token[mode];
  }
  return result;
}

export function getPalette(mode: Mode): ThemePalette {
  return Object.fromEntries(
    Object.entries(tokens).map(([group, shades]) => [
      group,
      resolveGroup(shades as Record<string, ColorToken>, mode),
    ])
  ) as ThemePalette;
}

type ThemePaletteProviderProps = {
  children: React.ReactNode;
  override?: Mode;
  style?: StyleProp<ViewStyle>;
};

// Named "ThemePalette" (not "Theme") to avoid conflicting with any ThemeProvider imports.
export default function ThemePaletteProvider({
  children,
  override,
  style = { flex: 1 },
}: ThemePaletteProviderProps) {
  const { colorScheme } = useColorScheme();
  const mode: Mode = override ?? colorScheme ?? 'light';

  const themeVars = useThemeVars(mode);
  const palette = useMemo(() => getPalette(mode), [mode]);

  return (
    <ThemeModeContext.Provider value={mode}>
      <ThemePaletteContext.Provider value={palette}>
        <View style={[themeVars, { colorScheme: mode }, style]}>{children}</View>
      </ThemePaletteContext.Provider>
    </ThemeModeContext.Provider>
  );
}
